'use client'

import type {ReactNode} from 'react'
import {MapPin, Ticket} from 'lucide-react'
import {formatActivitySession, dateTimeForLocale} from '@fitro/date-time/display'
import {DetailsFact, DetailsFactIcon, DetailsFacts, DetailsPeopleIcon} from '../shared/DetailsFact'
import {useTranslation} from '../../../hooks/useTranslation'
import type {PublicScheduleEvent} from '../../../services/public-schedule.service'
import {resolvePublicAssetUrl} from './publicAssetUrl'
import {LowestPrice30dNote} from '../promotions/PriceNotes'
import {publicEventPrice, reductionPercent} from './publicEventPrice'

/** The session of a Term as visitors see it everywhere: the hours first, then the date. */
export function usePublicTermSession(event: Pick<PublicScheduleEvent, 'timeZone' | 'startDateTime' | 'endDateTime'>) {
    const {dateTime} = useTranslation('landing')
    const session = formatActivitySession(dateTimeForLocale(dateTime.locale, event.timeZone), event.startDateTime, event.endDateTime)
    return {session, label: session.combinedLabel}
}

/** What one entry costs a visitor: the amount (a reduction struck through with its 30-day reference) and card prices. */
export function usePublicTermPrice(event: PublicScheduleEvent) {
    const {t, formatCurrency} = useTranslation('landing')
    const entryPrice = publicEventPrice(event)
    // A passes-only class has no price to show: say how to book instead.
    const price = entryPrice?.kind === 'passes'
        ? t('publicEntry.events.passesOnly')
        : entryPrice?.kind === 'free'
            ? t('publicEntry.events.free')
            : entryPrice
                ? formatCurrency(entryPrice.amountMinor / 100, {currency: entryPrice.currency})
                : null
    // An automatic promotion everyone gets is shown with its Omnibus reference, as in the booking chooser.
    const was = entryPrice?.kind === 'paid' && entryPrice.lowestPrice30dMinor !== null
        ? formatCurrency(entryPrice.lowestPrice30dMinor / 100, {currency: entryPrice.currency})
        : null
    const percent = was && entryPrice?.kind === 'paid' && entryPrice.lowestPrice30dMinor ? reductionPercent(entryPrice.amountMinor, entryPrice.lowestPrice30dMinor) : 0
    // The price paid leads; the reduction is struck through after it and its percentage stands out.
    const priceNode = was ? (
        <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-semibold tabular-nums">{price}</span>
            <s className="font-normal text-[var(--landing-neutral-500)]">{was}</s>
            {percent > 0 ? <span className="rounded-full bg-orange-50 px-2 py-0.5 text-xs font-medium leading-4 text-orange-800 dark:bg-orange-950 dark:text-orange-200">−{percent}%</span> : null}
        </span>
    ) : price
    const lowestPriceNote = was ? <LowestPrice30dNote price={was} className="mt-0.5 text-xs font-normal leading-4 text-[var(--landing-neutral-500)]" /> : null
    const firstEntry = entryPrice?.kind === 'paid' && entryPrice.firstEntryMinor !== null
        ? formatCurrency(entryPrice.firstEntryMinor / 100, {currency: entryPrice.currency})
        : null
    const firstEntryNote = firstEntry ? <p className="mt-0.5 text-xs font-semibold leading-4 text-[var(--landing-primary-600)]">{t('publicEntry.events.firstEntry', {amount: firstEntry})}</p> : null
    const benefitCardLines = (event.benefitCardPrices ?? []).map(({cardName, priceMinor}) => priceMinor === 0
        ? t('publicEntry.events.benefitCardFree', {card: cardName})
        : t('publicEntry.events.benefitCard', {card: cardName, amount: formatCurrency(priceMinor / 100, {currency: 'PLN'})}))
    const benefitCards = benefitCardLines.map((line, index) => <p key={`${index}:${line}`} className="mt-0.5 text-xs font-normal leading-4 text-gray-500">{line}</p>)
    return {price, priceNode, lowestPriceNote, firstEntryNote, benefitCards}
}

/**
 * Who leads the Term, where and for how much, as icon facts: a `row` in the details and booking
 * windows, a `column` on a Term page where more facts follow (`children`, e.g. availability).
 * `price` replaces the price value; null leaves the price out. `muted` grays the icons of a Term
 * that cannot be booked, as its disabled action.
 */
export function PublicTermDetails({event, price: priceOverride, priceClassName, layout = 'row', muted = false, className, children}: {
    event: PublicScheduleEvent
    price?: ReactNode | null
    priceClassName?: string
    layout?: 'row' | 'column'
    muted?: boolean
    className?: string
    children?: ReactNode
}) {
    const {t} = useTranslation('landing')
    const {price, priceNode, lowestPriceNote, firstEntryNote, benefitCards} = usePublicTermPrice(event)
    const instructors = event.instructors.flatMap((instructor, index) => {
        const name = [instructor.firstName, instructor.lastName].filter(Boolean).join(' ').trim()
        return name ? [{key: `${name}-${index}`, name, avatarUrl: resolvePublicAssetUrl(instructor.avatarUrl)}] : []
    })
    const location = event.location?.name?.trim()
    const priceValue = priceOverride !== undefined
        ? priceOverride
        : price || benefitCards.length ? <>
            {price ? <span data-testid="public-event-price">{priceNode}</span> : null}
            {lowestPriceNote}
            {firstEntryNote}
            {benefitCards}
        </> : null
    return (
        <DetailsFacts layout={layout} className={className}>
            {instructors.length ? <DetailsFact label={t('publicEntry.details.instructors')} icon={<DetailsPeopleIcon people={instructors} muted={muted} max={layout === 'column' ? 1 : 2}/>}>
                {instructors.map(instructor => instructor.name).join(', ')}
            </DetailsFact> : null}
            {location ? <DetailsFact label={t('publicEntry.details.location')} icon={<DetailsFactIcon muted={muted}><MapPin className="h-4 w-4"/></DetailsFactIcon>}>
                {location}
            </DetailsFact> : null}
            {priceValue ? <DetailsFact className={priceClassName} label={t('publicEntry.events.price')} icon={<DetailsFactIcon muted={muted}><Ticket className="h-4 w-4"/></DetailsFactIcon>}>
                {priceValue}
            </DetailsFact> : null}
            {children}
        </DetailsFacts>
    )
}
